import React, { useEffect, useMemo, useState } from "react";
import VendorLayout from "../../components/VendorLayout";
import { api, formatEUR, formatEURDecimal, formatDateFR } from "../../lib/api";
import { fleetLabelMaps, categoryLabel } from "../../lib/vehicleLabel";
import { Banknote, TrendingUp, TrendingDown, Target, Percent, Scale, BarChart3, CreditCard, ShieldCheck, ReceiptText, Wallet, ChevronDown } from "lucide-react";
import SegmentedPills from "../../components/ui/SegmentedPills";
import {
  SyntheseView, EncaissementsView, CautionsView, FacturesView, DepensesView,
  PeriodFilter, usePeriod, periodBounds, KpiTile, PERIOD_LABEL,
} from "../../components/pilotage/RevenueViews";
import { usePermissions } from "../../lib/permissions";
import { AccessDenied } from "../../components/AccessDenied";
import { SkeletonDashboard } from "../../components/ui/Skeleton";

const SUB_TABS = [
  { key: "synthese", label: "Synthèse", icon: BarChart3 },
  { key: "encaissements", label: "Encaissements", icon: CreditCard },
  { key: "cautions", label: "Cautions", icon: ShieldCheck },
  { key: "factures", label: "Factures", icon: ReceiptText },
  { key: "depenses", label: "Dépenses", icon: Wallet },
  { key: "rentabilite", label: "Rentabilité", icon: TrendingUp },
];

export default function VendorRevenue({ embedded = false }) {
  const { can, isOwner, loaded } = usePermissions();
  const denied = loaded && !isOwner && !can("billing", "read");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState(() => localStorage.getItem("jn_revenue_tab") || "synthese");
  const [navOpen, setNavOpen] = useState(false);
  const changeTab = (k) => { setSubTab(k); localStorage.setItem("jn_revenue_tab", k); };

  const reloadInvoices = () =>
    api.get("/vendor/invoices").then(({ data }) => setInvoices(data.invoices || [])).catch(() => {});
  const reloadPurchases = () =>
    api.get("/vendor/purchases").then(({ data }) => setPurchases(data.purchases || [])).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get("/vendor/stats"),
      api.get("/vendor/revenue"),
      api.get("/vendor/vehicles"),
      api.get("/vendor/transactions"),
      api.get("/vendor/invoices").catch(() => ({ data: {} })),
      api.get("/vendor/purchases").catch(() => ({ data: {} })),
      api.get("/vendor/maintenance").catch(() => ({ data: {} })),
      api.get("/vendor/credit-notes").catch(() => ({ data: {} })),
    ])
      .then(([s, r, v, t, i, p, m, cn]) => {
        setStats(s.data);
        setBookings(r.data.bookings || []);
        setVehicles(v.data.vehicles || []);
        setTransactions(t.data.transactions || []);
        setInvoices(i.data.invoices || []);
        setPurchases(p.data.purchases || []);
        setMaintenance(m.data.records || []);
        setCreditNotes(cn.data.credit_notes || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const content = denied ? (
    <AccessDenied module="à la facturation & aux revenus" />
  ) : (
    <div className={embedded ? "p-4 sm:p-6" : "max-w-[1600px] mx-auto p-6 sm:p-8 lg:p-12"}>
      {loading ? (
        <div className="py-6">
          <SkeletonDashboard testid="revenue-skeleton" />
        </div>
      ) : (
        <>
          {/* Mobile : sélecteur compact des sous-onglets */}
          <div className="lg:hidden relative" data-testid="revenue-subtabs-mobile">
            {(() => {
              const cur = SUB_TABS.find((t) => t.key === subTab) || SUB_TABS[0];
              const CurIcon = cur.icon;
              return (
                <button onClick={() => setNavOpen((o) => !o)} className="w-full inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-white border border-slate-200 text-sm font-semibold text-slate-800" data-testid="revenue-subtab-dropdown">
                  <CurIcon className="h-4 w-4 text-violet-600" />
                  <span className="flex-1 text-left">{cur.label}</span>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${navOpen ? "rotate-180" : ""}`} />
                </button>
              );
            })()}
            {navOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setNavOpen(false)} />
                <div className="absolute z-30 mt-1 left-0 right-0 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                  {SUB_TABS.map((t) => (
                    <button key={t.key} onClick={() => { changeTab(t.key); setNavOpen(false); }} className={`w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-left ${subTab === t.key ? "bg-violet-50 text-violet-700" : "text-slate-700 hover:bg-slate-50"}`} data-testid={`revenue-subtab-item-${t.key}`}>
                      <t.icon className="h-4 w-4" /> {t.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Desktop : onglets classiques */}
          <nav className="hidden lg:flex items-center gap-1 border-b border-slate-200 overflow-x-auto no-scrollbar" data-testid="revenue-subtabs">
            {SUB_TABS.map((t) => (
              <button key={t.key} onClick={() => changeTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-4 h-11 text-xs font-bold whitespace-nowrap border-b-2 -mb-px transition-colors ${subTab === t.key ? "border-violet-600 text-violet-700" : "border-transparent text-slate-400 hover:text-slate-700"}`}
                data-testid={`revenue-tab-${t.key}`}>
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </nav>

          <div className="mt-6">
            {subTab === "synthese" && (
              <SyntheseView stats={stats} bookings={bookings} transactions={transactions}
                invoices={invoices} purchases={purchases} maintenance={maintenance} creditNotes={creditNotes} />
            )}
            {subTab === "encaissements" && <EncaissementsView transactions={transactions} bookings={bookings} />}
            {subTab === "cautions" && <CautionsView bookings={bookings} />}
            {subTab === "factures" && <FacturesView invoices={invoices} onReload={reloadInvoices} />}
            {subTab === "depenses" && (
              <DepensesView purchases={purchases} maintenance={maintenance} onChanged={reloadPurchases} />
            )}
            {subTab === "rentabilite" && <RentabiliteView bookings={bookings} vehicles={vehicles} />}
          </div>
        </>
      )}
    </div>
  );

  return embedded ? content : <VendorLayout>{content}</VendorLayout>;
}

// ─── 📈 Rentabilité ───────────────────────────────────────────────────
const overlapDays = (bStart, bEnd, winStart, winEnd) => {
  const s = bStart > winStart ? bStart : winStart;
  const e = bEnd < winEnd ? bEnd : winEnd;
  if (e < s) return 0;
  return Math.max(0, Math.round((e - s) / 86400000) + 1);
};

function RentabiliteView({ bookings, vehicles }) {
  const [period, setPeriod] = usePeriod();
  const [scope, setScope] = useState("all");
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [productStats, setProductStats] = useState(null);
  const [catMap, setCatMap] = useState({});

  useEffect(() => {
    api.get("/vendor/settings").then((r) => setCatMap(fleetLabelMaps(r.data?.fleet || {}).catMap)).catch(() => {});
  }, []);

  const [periodStart, periodEnd, daysInPeriod, monthsInPeriod] = useMemo(() => {
    const [start, end] = periodBounds(period);
    const d = Math.max(1, Math.round((end - start) / 86400000) + 1);
    let m;
    if (period.mode === "day") m = 1 / 30;
    else if (period.mode === "week") m = 7 / 30;
    else if (period.mode === "month") m = 1;
    else if (period.mode === "year") m = 12;
    else m = d / 30;
    return [start, end, d, m];
  }, [period]);

  const categories = useMemo(() => {
    const s = new Set();
    vehicles.forEach((v) => { if (v.category) s.add(v.category); });
    return [...s];
  }, [vehicles]);

  useEffect(() => {
    const iso = (d) => d.toISOString().slice(0, 10);
    api.get("/vendor/products/stats", { params: { date_from: iso(periodStart), date_to: iso(periodEnd) } })
      .then(({ data }) => setProductStats(data))
      .catch(() => setProductStats(null));
  }, [periodStart, periodEnd]);

  const scopedVehicles = useMemo(() => {
    if (scope === "vehicle" && selectedVehicle) return vehicles.filter((v) => v.id === selectedVehicle);
    if (scope === "category" && selectedCategory) return vehicles.filter((v) => v.category === selectedCategory);
    return vehicles;
  }, [scope, selectedVehicle, selectedCategory, vehicles]);

  const scopedVehicleIds = useMemo(() => new Set(scopedVehicles.map((v) => v.id)), [scopedVehicles]);

  const filteredBookings = useMemo(() => bookings.filter((b) => {
    const created = new Date(b.created_at);
    if (created < periodStart || created > periodEnd) return false;
    return scopedVehicleIds.has(b.vehicle_id);
  }), [bookings, periodStart, periodEnd, scopedVehicleIds]);

  const kpis = useMemo(() => {
    const gross = filteredBookings.reduce((a, b) => a + (b.subtotal || 0), 0);
    const net = filteredBookings.reduce((a, b) => a + (b.vendor_revenue || 0), 0);

    const monthlyCharges = scopedVehicles.reduce((acc, v) => {
      const c = v.costs || {};
      return acc + (Number(c.monthly_financing) || 0) + (Number(c.insurance) || 0) + (Number(c.maintenance) || 0) + (Number(c.miscellaneous) || 0);
    }, 0);
    const charges = monthlyCharges * monthsInPeriod;
    const margin = net - charges;

    let rentedDays = 0;
    scopedVehicles.forEach((v) => {
      bookings.forEach((b) => {
        if (b.vehicle_id !== v.id) return;
        if (b.status && !["confirmed", "completed"].includes(b.status)) return;
        const bs = new Date(b.start_date + "T00:00:00");
        const be = new Date(b.end_date + "T00:00:00");
        rentedDays += overlapDays(bs, be, periodStart, periodEnd);
      });
    });
    const capacity = daysInPeriod * scopedVehicles.length;
    const occupancy = capacity > 0 ? (rentedDays / capacity) * 100 : 0;

    return { gross, net, charges, monthlyCharges, margin, occupancy, rentedDays, capacity };
  }, [filteredBookings, scopedVehicles, bookings, monthsInPeriod, periodStart, periodEnd, daysInPeriod]);

  return (
    <div data-testid="revenue-rentabilite">
      <PeriodFilter value={period} onChange={setPeriod} testid="revenue-period" />

      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="fleet-profitability">
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pb-5 border-b border-slate-100">
          <div>
            <div className="overline">Aide à la décision</div>
            <h2 className="font-heading text-xl sm:text-2xl font-medium tracking-tight text-slate-900 mt-1">
              Rentabilité flotte
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              {scopedVehicles.length} véhicule{scopedVehicles.length > 1 ? "s" : ""} analysé{scopedVehicles.length > 1 ? "s" : ""}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <SegmentedPills
              items={[
                { key: "all", label: "Tous véhicules" },
                { key: "vehicle", label: "Par véhicule" },
                { key: "category", label: "Par catégorie" },
              ]}
              value={scope}
              onChange={(k) => {
                setScope(k);
                if (k === "vehicle" && !selectedVehicle && vehicles[0]) setSelectedVehicle(vehicles[0].id);
                if (k === "category" && !selectedCategory && categories[0]) setSelectedCategory(categories[0]);
              }}
              testid="revenue-scope"
            />
            {scope === "vehicle" && (
              <select value={selectedVehicle} onChange={(e) => setSelectedVehicle(e.target.value)}
                className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 focus:ring-2 focus:ring-violet-600 outline-none"
                data-testid="revenue-vehicle-select">
                <option value="">Sélectionner…</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.brand} {v.model}</option>)}
              </select>
            )}
            {scope === "category" && (
              <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}
                className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 focus:ring-2 focus:ring-violet-600 outline-none"
                data-testid="revenue-category-select">
                <option value="">Sélectionner…</option>
                {categories.map((c) => <option key={c} value={c}>{categoryLabel(c, catMap) || "Sans catégorie"}</option>)}
              </select>
            )}
          </div>
        </header>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
          <KpiTile label="Revenu brut" value={formatEUR(kpis.gross)} icon={TrendingUp} hint="CA total encaissé (TTC)" tone="neutral" testid="kpi-gross" />
          <KpiTile label="Revenu net" value={formatEUR(kpis.net)} icon={Banknote} hint="Reversé sur votre compte" tone="primary" testid="kpi-net" />
          <KpiTile label="Charges" value={formatEUR(kpis.charges)} icon={TrendingDown} hint={`Coûts fixes flotte · ${formatEUR(kpis.monthlyCharges)}/mois`} tone="warning" testid="kpi-charges" />
          <KpiTile label="Marge estimée" value={formatEUR(kpis.margin)} icon={Scale} hint={kpis.margin >= 0 ? "Bénéfice dégagé" : "Déficit sur la période"} tone={kpis.margin >= 0 ? "success" : "danger"} testid="kpi-margin" />
          <KpiTile label="Taux d'occupation" value={`${kpis.occupancy.toFixed(1)} %`} icon={Percent} hint={`${kpis.rentedDays} j loués / ${kpis.capacity} j dispo`} tone="neutral" testid="kpi-occupancy" />
          <KpiTile label="Seuil de rentabilité" value={formatEUR(kpis.charges)} icon={Target}
            hint={kpis.net >= kpis.charges ? "Seuil atteint ✓" : `Il manque ${formatEUR(Math.max(0, kpis.charges - kpis.net))}`}
            tone={kpis.net >= kpis.charges ? "success" : "neutral"} testid="kpi-breakeven" />
        </div>
      </section>

      {/* Rentabilité Produits & Options — consolidée (dérivée des réservations) */}
      {productStats && productStats.stats.some((s) => s.sales > 0) && (
        <section className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="products-profitability">
          <div className="p-6 border-b border-slate-100 flex items-end justify-between flex-wrap gap-2">
            <div>
              <div className="overline">Options</div>
              <h3 className="font-heading text-xl font-medium tracking-tight text-slate-900">Rentabilité des options · {PERIOD_LABEL[period.mode]}</h3>
            </div>
            <div className="text-sm font-semibold text-violet-700" data-testid="products-profit-total">
              Marge totale : {formatEURDecimal(productStats.totals.margin)}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">Option</th>
                  <th className="text-right px-6 py-3 font-semibold">Ventes</th>
                  <th className="text-right px-6 py-3 font-semibold">CA</th>
                  <th className="text-right px-6 py-3 font-semibold">Coûts internes</th>
                  <th className="text-right px-6 py-3 font-semibold">Marge</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {productStats.stats.filter((s) => s.sales > 0).map((s) => (
                  <tr key={s.product_id} data-testid={`product-profit-row-${s.product_id}`}>
                    <td className="px-6 py-3">
                      <span className="font-medium text-slate-900">{s.name}</span>
                      {s.category && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">{s.category}</span>}
                    </td>
                    <td className="px-6 py-3 text-right">{s.sales}</td>
                    <td className="px-6 py-3 text-right font-medium">{formatEURDecimal(s.revenue)}</td>
                    <td className="px-6 py-3 text-right text-slate-500">{s.cost > 0 ? `− ${formatEURDecimal(s.cost)}` : "—"}</td>
                    <td className={`px-6 py-3 text-right font-semibold ${s.margin >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatEURDecimal(s.margin)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <div className="overline">Historique</div>
            <h3 className="font-heading text-xl font-medium tracking-tight text-slate-900">
              Réservations · {PERIOD_LABEL[period.mode]}
            </h3>
          </div>
          {filteredBookings.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-400">Aucune réservation sur la période.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-6 py-3 font-semibold">Date</th>
                    <th className="text-left px-6 py-3 font-semibold">Véhicule</th>
                    <th className="text-left px-6 py-3 font-semibold">Client</th>
                    <th className="text-right px-6 py-3 font-semibold">Brut</th>
                    <th className="text-right px-6 py-3 font-semibold">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredBookings.map((b) => (
                    <tr key={b.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-slate-900">{formatDateFR(b.created_at.substring(0, 10))}</td>
                      <td className="px-6 py-4 max-w-[200px] truncate">{b.vehicle_name}</td>
                      <td className="px-6 py-4">{b.client_name}</td>
                      <td className="px-6 py-4 text-right font-medium">{formatEURDecimal(b.subtotal)}</td>
                      <td className="px-6 py-4 text-right font-semibold text-violet-600">{formatEURDecimal(b.vendor_revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 self-start">
          <div className="overline">Vue rapide</div>
          <h3 className="font-heading text-xl font-medium tracking-tight text-slate-900">Synthèse</h3>
          <div className="mt-4 space-y-3 text-sm">
            <Row label="Réservations sur la période" value={filteredBookings.length} />
            <Row label="Panier moyen" value={filteredBookings.length ? formatEURDecimal(kpis.gross / filteredBookings.length) : "—"} />
            <Row label="Revenu/jour loué" value={kpis.rentedDays ? formatEURDecimal(kpis.net / kpis.rentedDays) : "—"} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-slate-900">{value}</span>
    </div>
  );
}
